import { collection, doc, getDoc, getDocs, query, where, setDoc, updateDoc, deleteDoc, orderBy, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';
import { GlossarySourceCategory } from '../types/glossary';

/**
 * Service for managing glossary source categories using Firestore directly
 */
class GlossaryCategoryService {
  private CATEGORIES_COLLECTION = 'glossarySourceCategories';

  /**
   * Get all categories
   */
  async getAllCategories(): Promise<GlossarySourceCategory[]> {
    try {
      const categoriesSnapshot = await getDocs(
        query(collection(db, this.CATEGORIES_COLLECTION), orderBy('order', 'asc'))
      );
      
      return categoriesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          gid: data.gid,
          isActive: data.isActive,
          order: data.order,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        } as GlossarySourceCategory;
      });
    } catch (error) {
      throw new Error('Failed to fetch categories');
    }
  }

  /**
   * Get active categories only
   */
  async getActiveCategories(): Promise<GlossarySourceCategory[]> {
    try {
      const categoriesSnapshot = await getDocs(
        query(
          collection(db, this.CATEGORIES_COLLECTION), 
          where('isActive', '==', true),
          orderBy('order', 'asc')
        )
      );
      
      return categoriesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          gid: data.gid,
          isActive: data.isActive,
          order: data.order,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        } as GlossarySourceCategory;
      });
    } catch (error) {
      throw new Error('Failed to fetch active categories');
    }
  }

  /**
   * Create a new category
   */
  async createCategory(categoryData: Omit<GlossarySourceCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<GlossarySourceCategory> {
    try {
      // Check if GID already exists
      const existingSnapshot = await getDocs(
        query(collection(db, this.CATEGORIES_COLLECTION), where('gid', '==', categoryData.gid))
      );
      
      if (!existingSnapshot.empty) {
        throw new Error(`Category with GID ${categoryData.gid} already exists`);
      }

      const categoryRef = doc(collection(db, this.CATEGORIES_COLLECTION));
      const now = serverTimestamp();
      
      const newCategory = {
        ...categoryData,
        spreadsheetId: categoryData.spreadsheetId || '1TeGGJnyYBvkw-iRLC6oTz6VHqsS8gnjcgBVJyXpv0nY', // Default spreadsheet ID
        createdBy: categoryData.createdBy || 'admin', // Default creator
        createdAt: now,
        updatedAt: now
      };
      
      await setDoc(categoryRef, newCategory);
      
      // Return the created category with the generated ID
      return {
        id: categoryRef.id,
        ...newCategory,
        createdAt: now,
        updatedAt: now
      } as GlossarySourceCategory;
    } catch (error) {
      throw new Error('Failed to create category');
    }
  }

  /**
   * Update an existing category
   */
  async updateCategory(categoryId: string, updates: Partial<Omit<GlossarySourceCategory, 'id' | 'createdAt'>>): Promise<GlossarySourceCategory> {
    try {
      const categoryRef = doc(db, this.CATEGORIES_COLLECTION, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      
      if (!categoryDoc.exists()) {
        throw new Error(`Category ${categoryId} does not exist`);
      }

      // If updating GID, check for conflicts
      if (updates.gid) {
        const existingSnapshot = await getDocs(
          query(
            collection(db, this.CATEGORIES_COLLECTION), 
            where('gid', '==', updates.gid)
          )
        );
        
        // Check if any other category has this GID
        const conflictingDoc = existingSnapshot.docs.find(doc => doc.id !== categoryId);
        if (conflictingDoc) {
          throw new Error(`Category with GID ${updates.gid} already exists`);
        }
      }
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(categoryRef, updateData);
      
      // Return the updated category
      const updatedDoc = await getDoc(categoryRef);
      const data = updatedDoc.data()!;
      
      return {
        id: categoryId,
        name: data.name,
        gid: data.gid,
        isActive: data.isActive,
        order: data.order,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      } as GlossarySourceCategory;
    } catch (error) {
      throw new Error('Failed to update category');
    }
  }

  /**
   * Delete a category
   */
  async deleteCategory(categoryId: string): Promise<void> {
    try {
      const categoryRef = doc(db, this.CATEGORIES_COLLECTION, categoryId);
      const categoryDoc = await getDoc(categoryRef);
      
      if (!categoryDoc.exists()) {
        throw new Error(`Category ${categoryId} does not exist`);
      }
      
      await deleteDoc(categoryRef);
    } catch (error) {
      throw new Error('Failed to delete category');
    }
  }

  /**
   * Reorder categories
   */
  async reorderCategories(categoryId: string, newOrder: number): Promise<void> {
    try {
      const categoryRef = doc(db, this.CATEGORIES_COLLECTION, categoryId);
      
      await updateDoc(categoryRef, {
        order: newOrder,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw new Error('Failed to reorder category');
    }
  }
}

// Export a singleton instance
export const glossaryCategoryService = new GlossaryCategoryService();